import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { OrganisationSwitcherComponent } from 'app/layout/common/organisation-switcher/organisation-switcher.component';
import { SubscriptionWarningComponent } from 'app/shared/components/subscription-warning/subscription-warning.component';
import { MaintenanceBannerComponent } from 'app/shared/components/maintenance-banner/maintenance-banner.component';
import { combineLatest, forkJoin, Subject, takeUntil } from 'rxjs';
import { IRole, Organisation, OrganisationBasic } from 'app/core/organisation/organisation.types';
import { OrganisationService } from 'app/core/organisation/organisation.service';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { FuseConfigService } from '@fuse/services/config';
@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        FuseFullscreenComponent,
        UserComponent,
        RouterOutlet,
        OrganisationSwitcherComponent,
        SubscriptionWarningComponent,
        MaintenanceBannerComponent,
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('quickChat') quickChat: QuickChatComponent;
    isScreenSmall: boolean;
    isScreenMedium: boolean;
    navigation: Navigation;
    organisation: Organisation;
    user: User;
    userRole: IRole;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _organisationService: OrganisationService,
        private _userService: UserService,
        private _fuseConfigService: FuseConfigService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // user current organisation and navigation$ both
        combineLatest([
            this._organisationService.currentOrganisation$,
            this._navigationService.navigation$,
            this._userService.user$,
            this._userService.userRole$     
        ]).pipe(takeUntil(this._unsubscribeAll)).subscribe(([organisation, navigation, user, userRole]) => {
            if (organisation && navigation && user) {
                this.organisation = organisation as Organisation;
                this.userRole = userRole;
                // filter navigation items based on user role
                // check if group pattern is org-** then only add organisation slug to the link
                // if none of the group children have access to member then remove the group
                this.navigation = {
                    ...navigation,
                    default: navigation.default
                        .filter(group => 
                            userRole.name === 'ADMIN' || 
                            (group.access ? group.access.includes(userRole.name) : true) ||
                            (group.children && group.children.some(item => item.access.includes(userRole.name)))
                        )
                        .map(group => ({
                            ...group,
                            children: group.children
                                .filter(item => userRole.name === 'ADMIN' || item.access.includes(userRole.name))
                                .map(item => ({
                                    ...item,
                                    link: group.id.startsWith('org-') ? '/' + organisation.slug + item.link : item.link
                                }))
                        }))
                };
                this.user = user;
                //apply theme to fuse config from user preferences
                this._fuseConfigService.config = {
                    scheme: this.user.userPreferences?.find(preference => preference.key === 'theme')?.value as string
                };
            }
        });
        
        // Subscribe to the user service
        // this._userService.user$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((user: User) => {
        //         this.user = user;
        //     });
            
        // // Subscribe to navigation data
        // this._navigationService.navigation$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((navigation: Navigation) => {
        //         this.navigation = navigation;
        //     });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
                this.isScreenMedium = !matchingAliases.includes('lg');
            });

        // how to subscribe to the navigation changed event 
        this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((event) => {
            
            if (event instanceof NavigationEnd) {
                // get the current route
                const currentRoute = this._router.routerState.snapshot.url;
                
                // get the navigation item that matches the current route
                const navigationItem = this.navigation.default.find(item => item.link === currentRoute);
                // if (navigationItem) {
                //     // toggle the navigation item
                //     this.toggleNavigation(navigationItem.id);
                // }
            }
        });
    }


    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
